import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from './ui/button'
import { Input } from './ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { sendExistingAccountLink } from '../lib/api'

interface Props {
  /** Masked email of the account found, such as j***@gmail.com. Null keeps the dialog closed. */
  maskedEmail: string | null
  name: string
  dateOfBirth: string
  onClose: () => void
  /** "That's not me": carry on creating a new account */
  onContinue: () => void
}

/**
 * Shown when the name and birthday being signed up match an existing athlete. Typing the full
 * email on that account (which proves it is theirs) emails them a sign-in link.
 */
const ExistingAccountDialog: React.FC<Props> = ({
  maskedEmail,
  name,
  dateOfBirth,
  onClose,
  onContinue,
}) => {
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSending(true)
    try {
      await sendExistingAccountLink(name, dateOfBirth, email)
      setSent(true)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={!!maskedEmail} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>You may already have an account</DialogTitle>
          <DialogDescription>
            We found an account for this name and birthday using {maskedEmail}.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <p className="text-sm">
            Check your inbox. We sent a sign-in link to <strong>{email.trim()}</strong>.
          </p>
        ) : (
          <form onSubmit={handleSend} className="space-y-3">
            <label className="text-sm font-medium block" htmlFor="existing-account-email">
              Enter the email on that account and we will send you a sign-in link
            </label>
            <Input
              id="existing-account-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <Button type="submit" variant="lime" className="w-full" disabled={sending || !email}>
              {sending ? 'Sending…' : 'Send sign-in link'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Prefer a password?{' '}
              <Link to="/signin" className="underline">
                Sign in
              </Link>
            </p>
          </form>
        )}

        <DialogFooter className="sm:justify-center">
          <Button type="button" variant="ghost" onClick={onContinue}>
            That's not me, continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ExistingAccountDialog
